
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import type { PurchaseListItem } from '@/types/purchase-list';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { isUsableImageUrl } from '@/helpers/variantImage';

@Component({})
export default class PurchaseListItemRow extends Vue {
  /**
   * The usable-image rule, exposed so the template can call it. A Vue class
   * component cannot call a module function from a template unless it is a
   * class property — without this the v-if evaluates `undefined` as a
   * function and Vue warns at runtime while the build stays green.
   */
  private isUsableImageUrl = isUsableImageUrl;

  @Prop({ required: true }) public readonly item!: PurchaseListItem;

  public readonly MAX_QUANTITY = 1000000;

  // Local mirror of item.quantity so the line total recomputes instantly
  // on every keystroke/blur, independent of how fast the parent wires the
  // API update back into the reactive item object.
  public localQuantity = 0;

  public created(): void {
    if (this.item) {
      this.localQuantity = Number(this.item.quantity) || 0;
    }
  }

  @Watch('item.quantity')
  public onPropQuantityChange(newVal: number | string): void {
    const n = Number(newVal);
    if (Number.isFinite(n) && n !== this.localQuantity) {
      this.localQuantity = n;
    }
  }

  public get lineTotal(): number {
    if (!this.item) { return 0; }
    return this.localQuantity * Number(this.item.unitCost);
  }

  public get formattedLineTotal(): string {
    if (!this.item) { return ''; }
    return CurrencyHelper.formatCurrencyWithCode(
      this.lineTotal,
      this.item.currencyCode || 'USD',
    );
  }

  public onQty(value: number | string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) { return; }
    const clamped = Math.min(Math.floor(parsed), this.MAX_QUANTITY);
    this.localQuantity = clamped;
    this.$emit('qty-change', clamped);
  }

  public onRemove(): void {
    this.$emit('remove');
  }
}
